import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * @typedef {Object} ScrollStackRefs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} progressFill
 */

/** @extends {Component<ScrollStackRefs>} */
export class ScrollStackComponent extends Component {
  requiredRefs = ['slides', 'progressFill'];

  /** @type {EventTarget | null} */
  #scrollTarget = null;
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion() || this.refs.slides.length === 0) return;

    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollTarget);
    this.#updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    scrollContainerMediaQuery.removeEventListener('change', this.#bindScrollTarget);
    this.#unbindScrollTarget();
  }

  #bindScrollTarget = () => {
    this.#unbindScrollTarget();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  };

  #unbindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateProgress();
    });
  };

  /**
   * One continuous bar for the whole stack (not per slide): 0 at the first
   * slide, 1 once the last slide has fully taken over. Each slide occupies
   * exactly one viewport height of scroll, so the last slide is fully
   * revealed after (slides.length - 1) viewports of scrolling — there's no
   * slide after it to scroll "through".
   */
  #updateProgress() {
    const { slides, progressFill } = this.refs;
    const viewportHeight = window.innerHeight;
    const scrolled = Math.max(0, -this.getBoundingClientRect().top);
    const totalScrollDistance = Math.max(1, (slides.length - 1) * viewportHeight);
    const progress = clamp(scrolled / totalScrollDistance, 0, 1);

    progressFill.style.setProperty('--at-scroll-stack-progress', String(progress));

    let activeIndex = 0;
    for (let i = 1; i < slides.length; i++) {
      const slideProgress = clamp(scrolled / viewportHeight - (i - 1), 0, 1);
      slides[i].style.setProperty('--at-scroll-stack-slide-progress', String(slideProgress));
      if (slideProgress > 0) activeIndex = i;
    }

    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === activeIndex));
  }
}

if (!customElements.get('scroll-stack-component')) {
  customElements.define('scroll-stack-component', ScrollStackComponent);
}
